import React, { forwardRef, useImperativeHandle } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormRef } from "types/form";
import TextBox from "components/TextBox";
import HelpHoverIcon from "components/HelpHoverIcon";
import { RootState } from "store";
import {
  setConfirmPassword,
  setEmail,
  setPassword,
  setUsername,
  setFormErrors,
  SignUpState,
} from "store/signUpSlice";
import { accountSchema } from "validation/schemas/account";
import AuthService from "api/Auth/AuthService";

import styles from "./AccountForm.module.scss";

const USERNAME_HELP_MESSAGE =
  "Username requirements:\n" +
  "- 5-15 characters long\n" +
  "- Can contain letters, numbers, periods, underscores, and hyphens\n";

const PASSWORD_HELP_MESSAGE =
  "Password requirements:\n" +
  "- 6-32 characters long\n" +
  "- Must contain uppercase letters\n" +
  "- Must contain lowercase letters\n" +
  "- Must contain special characters";

const AccountForm = forwardRef<FormRef>((props, ref) => {
  const dispatch = useDispatch();
  const signUpState: SignUpState = useSelector(
    (state: RootState) => state.signUp,
  );

  const clearErrors = () => {
    if (signUpState.errors?.account) {
      dispatch(setFormErrors({}));
    }
  };

  const onFormChange =
    (onChange: (value: string) => void) => (value: string) => {
      clearErrors();
      onChange(value);
    };

  useImperativeHandle(ref, () => ({
    validate: async () => {
      clearErrors();
      const result = accountSchema.safeParse({
        email: signUpState.email,
        username: signUpState.username,
        password: signUpState.password,
        confirmPassword: signUpState.confirmPassword,
      });

      if (!result.success) {
        const errorsByField = result.error.errors.reduce(
          (acc, curr) => {
            if (!acc[curr.path[0]]) {
              acc[curr.path[0]] = curr.message;
            }
            return acc;
          },
          {} as Record<string, string>,
        );

        dispatch(
          setFormErrors({
            account: errorsByField,
          }),
        );
        return false;
      }

      try {
        // Check both email and username existence simultaneously
        const [emailResponse, usernameResponse] = await Promise.all([
          AuthService.getEmailExists(signUpState.email),
          AuthService.getUsernameExists(signUpState.username),
        ]);

        const errors: Record<string, string> = {};

        if (emailResponse.exists) {
          errors.email = "Email already exists. Please use a different email.";
        }

        if (usernameResponse.exists) {
          errors.username =
            "Username already exists. Please use a different username.";
        }

        if (Object.keys(errors).length > 0) {
          dispatch(
            setFormErrors({
              account: errors,
            }),
          );
          return false;
        }

        dispatch(setFormErrors({}));
        return true;
      } catch (error) {
        throw new Error("Failed to verify account details. Please try again.");
      }
    },
  }));

  return (
    <div className={styles.form}>
      <div className={styles.field}>
        <div className={styles.label}>Email</div>
        <div className={styles.inputWrapper}>
          <TextBox
            lean
            placeholder="youremail@domain.com"
            value={signUpState.email}
            onChange={onFormChange((value) => dispatch(setEmail(value)))}
            error={!!signUpState.errors?.account?.email}
          />
          {signUpState.errors?.account?.email && (
            <span className={styles.errorText}>
              {signUpState.errors.account.email}
            </span>
          )}
        </div>
      </div>

      <div className={styles.field}>
        <div className={styles.label}>
          User Name
          <span className={styles.helpIcon}>
            <HelpHoverIcon message={USERNAME_HELP_MESSAGE} />
          </span>
        </div>
        <div className={styles.inputWrapper}>
          <TextBox
            lean
            placeholder="adamsmith23"
            value={signUpState.username}
            onChange={onFormChange((value) => dispatch(setUsername(value)))}
            error={!!signUpState.errors?.account?.username}
          />
          {signUpState.errors?.account?.username && (
            <div className={styles.errorText}>
              {signUpState.errors.account.username}
            </div>
          )}
        </div>
      </div>

      <div className={styles.field}>
        <div className={styles.label}>
          Password
          <span className={styles.helpIcon}>
            <HelpHoverIcon message={PASSWORD_HELP_MESSAGE} />
          </span>
        </div>
        <div className={styles.inputWrapper}>
          <TextBox
            lean
            password
            placeholder="your_password"
            value={signUpState.password}
            onChange={onFormChange((value) => dispatch(setPassword(value)))}
            error={!!signUpState.errors?.account?.password}
          />
          {signUpState.errors?.account?.password && (
            <div className={styles.errorText}>
              {signUpState.errors.account.password}
            </div>
          )}
        </div>
      </div>

      <div className={styles.field}>
        <div className={styles.label}>Confirm Password</div>
        <div className={styles.inputWrapper}>
          <TextBox
            lean
            password
            placeholder="your_password"
            value={signUpState.confirmPassword}
            onChange={onFormChange((value) =>
              dispatch(setConfirmPassword(value)),
            )}
            error={!!signUpState.errors?.account?.confirmPassword}
          />
          {signUpState.errors?.account?.confirmPassword && (
            <div className={styles.errorText}>
              {signUpState.errors.account.confirmPassword}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

AccountForm.displayName = "AccountForm";

export default AccountForm;
